import { Col, Form, FormListOperation, ModalProps, Row, Space } from 'antd';
import Card from 'app/components/Card';
import React from 'react';
import styled from '@emotion/styled';
import JobStatus from 'app/components/Status/JobStatus';
import moment from 'moment';
import Modal from 'app/components/Modal';
import InspectorScheduler from '../../Scheduler';
import CardFactoryBasicInfo from './components/CardFactoryInfo';
import StatusDescriptions from './components/StatusDescription';

import { Event } from 'react-big-calendar';
import TooltipEvent from './components/TooltipEvent';
import {
  useLazyGetInspectorAvailableQuery,
  useLazyGetInspectorWithJobQuery
} from 'app/features/inspection/api';
import { DateUtils } from 'app/utils';
import { PickerInspector } from './components/FormItems/PickerInspector';
import { isNumber, isString, sumBy } from 'lodash';

const StyledCard = styled(Card)`
  .ant-card-head {
    border-bottom: none;
    min-height: 20px;
  }
  .ant-card-head-title {
    padding-bottom: 0px;
  }
  .ant-card-body {
    padding-top: 8px;
    padding-bottom: 8px;
    flex-direction: column;
    display: flex;
  }
`;

const ModalAssignInspector = (
  props: Partial<ModalProps> & {
    onOk: (values: any) => void;
    inspectionId: string | number;
    assigned: Array<{
      inspectionDates: Array<{ date: moment.Moment; manDay: number }>;
      inspectorId: string | number;
      [key: string]: any;
    }>;
    factory: any;
    manDay: number;
    inspectionStart?: moment.Moment;
    inspectionEnd?: moment.Moment;
  }
) => {
  const [selectedInspector, setSelectedInspector] = React.useState(['all']);
  const [fetchInspectorWithJob, resultInspectorWithJob] =
    useLazyGetInspectorWithJobQuery();
  const [fetchInspectorAvailable] = useLazyGetInspectorAvailableQuery();
  const operationRef = React.useRef<FormListOperation>();
  const {
    factory,
    manDay,
    inspectionStart,
    inspectionEnd,
    onOk,
    assigned,
    inspectionId,
    ...modalProps
  } = props;
  const [form] = Form.useForm();

  const [rangeDates, setRangeDates] = React.useState<Array<Date>>([]);

  const getEvent = (scheduled: any[]): Event[] => {
    if (!scheduled || !scheduled.length) return [];

    return scheduled.reduce((acc, item) => {
      const events = item?.inspectors?.map(({ inspector, manDay }: any) => {
        return {
          title: <>{`${inspector.firstName} ${inspector.lastName}`}</>,
          start: item?.inspectionDate.toDate(),
          end: item?.inspectionDate.toDate(),
          allDay: true,
          resource: {
            ...item,
            manDay: manDay,
            factory: factory,
            status: 'waitingAccept'
          }
        };
      });
      return acc.concat(events);
    }, []);
  };
  const getEvents = (currentEventJob: any[]) => {
    // eslint-disable-next-line no-debugger
    const events = resultInspectorWithJob?.data?.data || [];
    const excludeCurrentInspectionEvents = events.filter(
      (event) => event.inspection.id != inspectionId
    );
    const currentEvent = getEvent(currentEventJob);
    return currentEvent.concat(
      excludeCurrentInspectionEvents.map((event) => {
        return {
          title: (
            <>{`${event.inspector?.firstName} ${event.inspector?.lastName}`}</>
          ),
          start: moment(event.inspectionDate).toDate(),
          end: moment(event.inspectionDate).toDate(),
          allDay: true,
          resource: {
            manDay: event.totalWorkday,
            inspectionDates: event?.workdays?.map((date: any) => ({
              date: moment(date)
            })),
            factory: event.inspection.factory,
            status: event?.inspection?.status,
            inspectorName: `${event.inspector?.firstName} ${event.inspector?.lastName}`
          }
        };
      })
    );
  };

  const handleInitAssignTo = async (defaultAssigned: any[]) => {
    const dates: any[] = [];
    const maxDates = DateUtils.getNumOfDays(inspectionStart, inspectionEnd);

    for (let i = 0; i <= maxDates; i++) {
      const currentDate = inspectionStart?.clone().add(i, 'day');
      const inspectors = defaultAssigned.reduce((acc, current) => {
        const { inspectionDates, inspector } = current;
        const scheduled = inspectionDates.find(
          ({ date }) =>
            currentDate.format('DDMMYYYY') === date.format('DDMMYYYY')
        );
        if (scheduled) {
          acc.push({
            inspector,
            manDay: scheduled.manDay
          });
        }
        return acc;
      }, []);

      dates.push({
        inspectionDate: currentDate,
        inspectors: inspectors
      });
    }
    form.setFieldValue('assignTo', dates);
  };

  const handleSubmitForm = (values) => {
    const { assignTo = [] } = values;
    const manDayValue = Number(manDay);

    const totalManday = assignTo.reduce((acc, current) => {
      if (current.inspectors.length) {
        const manDays = sumBy(
          current.inspectors,
          ({ manDay: manDayOfInspector }) => {
            if (
              isString(manDayOfInspector) &&
              isNaN(Number(manDayOfInspector))
            ) {
              return Number(manDayOfInspector);
            }
            if (isNumber(manDayOfInspector)) {
              return manDayOfInspector;
            }
            return 0;
          }
        );
        return acc + manDays;
      }
      return acc;
    }, 0);

    // Round totalMandayValue to 2 decimal places
    const totalMandayValue = parseFloat(totalManday.toFixed(2));
    const roundedManDayValue = parseFloat(manDayValue.toFixed(2)); // Ensure manDayValue is also rounded

    if (totalMandayValue > roundedManDayValue) {
      Modal.danger({
        onCancel: () => {},
        title: 'Manday not match',
        content: 'Please arrange again.'
      });
      return;
    }
    if (onOk) {
      onOk(values);
    }
  };

  React.useEffect(() => {
    if (inspectionStart && inspectionEnd) {
      fetchInspectorAvailable({
        current: 1,
        pageSize: 99999,
        startDate: inspectionStart.format('YYYY-MM-DD'),
        endDate: inspectionEnd.format('YYYY-MM-DD')
      });
      setRangeDates([
        inspectionStart.clone().toDate(),
        inspectionEnd.clone().toDate()
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectionStart, inspectionEnd]);

  React.useEffect(() => {
    if (rangeDates.length > 1) {
      const params = {
        current: 1,
        pageSize: 99999,
        startDate: moment(rangeDates[0])
          .clone()
          .subtract(5, 'day')
          .format('YYYY-MM-DD'),

        endDate: moment(rangeDates[1])
          .clone()
          .add(5, 'day')
          .format('YYYY-MM-DD'),
        inspectorIds: selectedInspector.filter((value) => value !== 'all')
      };
      if (selectedInspector.includes('all')) {
        delete (params as any)['inspectorIds'];
      }
      fetchInspectorWithJob(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInspector, rangeDates]);

  React.useEffect(() => {
    if (props.open) {
      handleInitAssignTo(assigned);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, assigned]);

  return (
    <>
      <Modal
        title="Select Inspector"
        width={'90vw'}
        onOk={() => {
          form.submit();
        }}
        centered
        {...modalProps}
      >
        <Form
          onFinish={handleSubmitForm}
          form={form}
          initialValues={{
            assignTo: []
          }}
        >
          <Row gutter={8} wrap>
            <Col span={18}>
              <Form.Item shouldUpdate>
                {(instance) => {
                  return (
                    <InspectorScheduler
                      onChangeInspector={(selected) => {
                        setSelectedInspector(selected);
                      }}
                      selectedInspector={selectedInspector}
                      startDate={inspectionStart?.toDate()}
                      endDate={inspectionEnd?.toDate()}
                      value={rangeDates[0] || inspectionStart?.toDate()}
                      events={getEvents(instance.getFieldValue('assignTo'))} //instance.getFieldValue('assignTo')
                      onRangChange={(dates) => {
                        const allDates = dates as Date[];
                        setRangeDates([
                          allDates[0],
                          allDates[allDates.length - 1]
                        ]);
                      }}
                      renderEvent={(eventProps) => {
                        const { event } = eventProps;

                        return (
                          <TooltipEvent
                            factory={event.resource.factory}
                            event={event}
                          >
                            <Space
                              style={{ width: '100%' }}
                              align="center"
                              size={0}
                            >
                              <JobStatus
                                style={{ fontSize: 10 }}
                                alpha={1}
                                status={(event as any).resource.status}
                              >
                                PW
                              </JobStatus>
                              {eventProps?.title}
                            </Space>
                          </TooltipEvent>
                        );
                      }}
                    ></InspectorScheduler>
                  );
                }}
              </Form.Item>

              <StatusDescriptions
                descriptions={[
                  {
                    status: 'waiting',
                    label: 'PW',
                    color: '#FFC625',
                    description: 'Waiting to Accept '
                  },
                  {
                    status: 'scheduled',
                    label: 'PW',
                    color: '#005EFF',
                    description: 'Scheduled'
                  }
                ]}
              ></StatusDescriptions>
            </Col>
            <Col span={6}>
              <CardFactoryBasicInfo
                info={{
                  factoryLocation: factory?.address,
                  factoryName: factory?.name,
                  manDay: manDay,
                  start: inspectionStart,
                  end: inspectionEnd
                }}
              ></CardFactoryBasicInfo>
              <Form.Item shouldUpdate>
                {(formInstance) => (
                  <StyledCard title="Select Inspector(s)" bordered={false}>
                    <Form.List name="assignTo">
                      {(fields, operation) => {
                        operationRef.current = operation;

                        return (
                          <>
                            <div
                              style={{ maxHeight: 250, overflowY: 'scroll' }}
                            >
                              {fields.map((item) => {
                                const schedule = form.getFieldValue([
                                  'assignTo',
                                  item.name
                                ]);
                                const assigned = formInstance.getFieldValue([
                                  'assignTo',
                                  item.name
                                ]);
                                return (
                                  <PickerInspector
                                    jobManday={manDay}
                                    assigned={assigned?.inspectors || []}
                                    startDate={schedule?.inspectionDate}
                                    endDate={inspectionEnd}
                                    key={item.name}
                                    name={item.name}
                                    title={schedule?.inspectionDate?.format(
                                      'DD MMM YYYY'
                                    )}
                                    events={getEvents([
                                      {
                                        inspectionDate: schedule?.inspectionDate
                                      }
                                    ])?.filter((value) => !!value)}
                                  />
                                );
                              })}
                            </div>
                          </>
                        );
                      }}
                    </Form.List>
                  </StyledCard>
                )}
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default ModalAssignInspector;
